import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Button, Col, Input, Modal, Row, Spin, Table, Tag } from 'antd'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  approveDelegatedClaim,
  getDelegatedClaimsDocument,
  getDelegatedClaimsList,
  rejectDelegatedClaim,
} from '../../api/delegatedClaims.api'

const DelegatedClaimsPage = () => {
  const [delegationData, setDelegationData] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: 10 },
    totalElements: 0,
  })
  const [expandedRowKeys, setExpandedRowKeys] = useState([])
  const [detailData, setDetailData] = useState({})
  const [loadingDetails, setLoadingDetails] = useState({})
  const [loadingStatus, setLoadingStatus] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [reject, setReject] = useState({
    isRejectModalOpen: false,
    rejectReason: '',
    currentId: null,
  })
  const navigate = useNavigate()

  // 생년월일 포맷팅 함수 (YYMMDD -> YYYY-MM-DD)
  const formatBirthday = (birthday) => {
    if (!birthday) return '-'
    const year = '19' + birthday.substring(0, 2)
    const month = birthday.substring(2, 4)
    const day = birthday.substring(4, 6)
    return `${year}-${month}-${day}`
  }

  useEffect(() => {
    fetchDelegatedClaimsList()
  }, [])
  const fetchDelegatedClaimsList = async (page = 0) => {
    setIsLoading(true)
    try {
      const res = await getDelegatedClaimsList({
        page: page,
        size: 10,
      })

      if (res.data.content.length > 0) {
        setDelegationData({
          content: res.data.content,
          pageable: res.data.pageable || { pageNumber: 0, pageSize: 10 },
          totalElements: res.data.totalElements || 0,
        })
      }
    } catch (error) {
      console.error('fetchDelegatedClaimsList error:', error)
      alert(`데이터를 불러오는데 실패했습니다: ${error.message}`)
    } finally {
      setIsLoading(false)
    }
  }

  // 위임장 승인
  const handleApproval = async (record) => {
    Swal.fire({
      title: '위임장 승인 하시겠습니까?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        await approveDelegatedClaim({ id: record.id })
        fetchDelegatedClaimsList()
      }
    })
  }

  // 위임장 반려
  const handleRejectModalOpen = (record) => {
    setReject({
      isRejectModalOpen: true,
      rejectReason: '',
      currentId: record.id,
    })
  }
  const handleRejectModalClose = () => {
    setReject({
      isRejectModalOpen: false,
      rejectReason: '',
      currentId: null,
    })
  }

  // 반려 확인
  const handleRejectConfirm = async () => {
    if (!reject.rejectReason?.trim()) {
      alert('반려 사유를 입력해주세요.')
      return
    }

    try {
      setIsLoading(true)
      await rejectDelegatedClaim({
        id: reject.currentId,
        rejectionReason: reject.rejectReason,
      })

      fetchDelegatedClaimsList()
      handleRejectModalClose()
    } catch (error) {
      console.error('반려 처리 실패:', error)
      alert('반려 처리 중 오류가 발생했습니다.')
    } finally {
      setIsLoading(false)
    }
  }

  // 테이블 컬럼
  const columns = [
    {
      title: '상태값',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => {
        const statusConfig = {
          SUBMITTED: { text: '위임 심사 대기', color: '#F59E0B' },
          APPROVED: { text: '위임 완료', color: '#00A4FF' },
          REJECTED: { text: '반려', color: '#EB516F' },
        }
        const config = statusConfig[status] || {
          text: status,
          color: '#6D747A',
        }
        return (
          <Tag
            color={config.color}
            style={{ color: '#FFF', fontWeight: 'bold' }}
          >
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: 'No',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '위임 타입',
      dataIndex: 'delegationType',
      key: 'delegationType',
      width: 120,
      render: (type) => {
        const typeConfig = {
          GENERAL: { text: '일반 위임', color: '#00A4FF' },
          LOW_INCOME: { text: '차상위 위임', color: '#115CB0' },
        }
        const config = typeConfig[type] || {
          text: type,
          color: '#6D747A',
        }
        return (
          <Tag
            color={config.color}
            style={{ color: '#FFF', fontWeight: 'bold' }}
          >
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: '신청 타입',
      dataIndex: 'submissionType',
      key: 'submissionType',
      width: 100,
      render: (type) => {
        const typeConfig = {
          SELF: { text: '본인', color: '#00A4FF' },
          LEGAL: { text: '법정 대리인', color: '#EB516F' },
        }
        const config = typeConfig[type] || {
          text: type,
          color: '#6D747A',
        }
        return (
          <Tag
            color={config.color}
            style={{ color: '#FFF', fontWeight: 'bold' }}
          >
            {config.text}
          </Tag>
        )
      },
    },
    {
      title: '수진자명',
      dataIndex: 'patientName',
      key: 'patientName',
      width: 100,
    },
    {
      title: '수진자 생년월일',
      dataIndex: 'patientBirthday',
      key: 'patientBirthday',
      width: 120,
      render: (birthday) => {
        if (!birthday) return '-'
        // YYMMDD 형식을 YYYY-MM-DD로 변환
        const year = '19' + birthday.substring(0, 2)
        const month = birthday.substring(2, 4)
        const day = birthday.substring(4, 6)
        return `${year}-${month}-${day}`
      }, // 바꿔야 함
    },
    {
      title: '신청자명',
      dataIndex: 'applicantName',
      key: 'applicantName',
      width: 100,
      render: (name) => name || '-',
    },
    {
      title: '신청 일자',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => {
        if (!date) return '-'
        return new Date(date).toLocaleDateString('ko-KR')
      },
    },
    {
      title: '위임 만료일자',
      dataIndex: 'delegationExpiredAt',
      key: 'delegationExpiredAt',
      width: 140,
      render: (date) => {
        if (!date) return '-'
        return new Date(date).toLocaleDateString('ko-KR')
      },
    },
    {
      title: '관리자 조치',
      key: 'managerActions',
      width: 180,
      render: (_, record) => {
        const isLoading = loadingStatus[record.key]

        // 관리자 처리 단계에서만 버튼 활성화
        const canManagerProcess = record.status === 'SUBMITTED'

        return (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="small"
              type={record.status === 'APPROVED' ? 'primary' : 'default'}
              style={{
                backgroundColor:
                  record.status === 'APPROVED'
                    ? '#00A4FF'
                    : canManagerProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'APPROVED'
                    ? '#00A4FF'
                    : canManagerProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'APPROVED'
                    ? '#FFF'
                    : canManagerProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'APPROVED'}
              disabled={!canManagerProcess || isLoading}
              onClick={() => handleApproval(record)}
            >
              위임 승인
            </Button>
            <Button
              size="small"
              type={record.status === 'REJECTED' ? 'primary' : 'default'}
              style={{
                backgroundColor:
                  record.status === 'REJECTED'
                    ? '#EB516F'
                    : canManagerProcess
                      ? '#FFF'
                      : '#F5F5F5',
                borderColor:
                  record.status === 'REJECTED'
                    ? '#EB516F'
                    : canManagerProcess
                      ? '#A4ADB4'
                      : '#D9D9D9',
                color:
                  record.status === 'REJECTED'
                    ? '#FFF'
                    : canManagerProcess
                      ? '#6D747A'
                      : '#BFBFBF',
              }}
              loading={isLoading === 'REJECTED'}
              disabled={!canManagerProcess || isLoading}
              onClick={() => handleRejectModalOpen(record)}
            >
              위임 반려
            </Button>
          </div>
        )
      },
    },
    {
      title: '상세',
      key: 'detail',
      width: 80,
      render: (_, record) => (
        <Button
          type="text"
          icon={
            expandedRowKeys.includes(record.id) ? (
              <UpOutlined />
            ) : (
              <DownOutlined />
            )
          }
          onClick={() => handleRowExpand(record.id)}
        />
      ),
    },
  ]

  // 위임장 서류 관련 정보 API 호출
  const fetchDelegationDetail = async (id) => {
    setLoadingDetails({ ...loadingDetails, [id]: true })
    try {
      const record = delegationData?.content?.find((item) => item.id === id)
      if (!record) {
        throw new Error('해당 데이터를 찾을 수 없습니다.')
      }

      const res = await getDelegatedClaimsDocument({ id })
      console.log('res', res)

      // attachments 데이터를 받아와서 각 파일을 적절한 위치에 매핑
      const attachments = res?.data?.attachments || []
      const document = res?.data?.document || null

      // 신분증과 차상위 계층 서류를 찾기 위한 함수
      const findAttachmentByKeyword = (keyword) => {
        return attachments.find(
          (attachment) => attachment.name && attachment.name.includes(keyword),
        )
      }

      const identityCardAttachment = findAttachmentByKeyword('신분증')
      const lowIncomeAttachment = findAttachmentByKeyword('차상위')

      const mockDetail = {
        delegationNumber: `DC-${id.toString().padStart(6, '0')}`,
        applicationDate: new Date(record.createdAt).toLocaleDateString('ko-KR'),
        delegationType:
          record.delegationType === 'GENERAL' ? '일반 위임' : '의료 위임',
        submissionType: record.submissionType === 'SELF' ? '본인' : '대리인',
        recipientInfo: `${record.recipientName} (생년월일: ${formatBirthday(
          record.recipientBirthday,
        )})`,
        applicantInfo: record.applicantName
          ? `${record.applicantName} (생년월일: ${formatBirthday(
              record.applicantBirthday,
            )})`
          : '-',
        relationshipType: record.applicantRelation || '-',
        delegationPurpose: '처방전 구매 및 수령',
        validityPeriod: `${new Date(record.createdAt).toLocaleDateString(
          'ko-KR',
        )} ~ ${new Date(record.delegationExpiredAt).toLocaleDateString(
          'ko-KR',
        )}`,
        // 서류 제출 상태와 다운로드 URL
        document: document, // 위임장 문서 URL
        documents: {
          identityCard: !!identityCardAttachment, // 신분증 첨부 여부
          identityCardUrl: identityCardAttachment?.file?.downloadUrl || null, // 신분증 다운로드 URL
          delegation: !!document, // 위임증
          delegationUrl: document || null, // 위임증 다운로드 URL
          lowIncomeProof: !!lowIncomeAttachment, // 차상위 증빙 서류 첨부 여부
          lowIncomeUrl: lowIncomeAttachment?.file?.downloadUrl || null, // 차상위 증빙 서류 다운로드 URL
        },
      }

      // Mock 데이터 설정 (실제 API 연동 시 제거)
      setTimeout(() => {
        setDetailData({ ...detailData, [id]: mockDetail })
        setLoadingDetails({ ...loadingDetails, [id]: false })
      }, 300) // 로딩 시뮬레이션
    } catch (error) {
      console.error('상세 정보 조회 실패:', error)
      setLoadingDetails({ ...loadingDetails, [id]: false })
      alert('상세 정보를 불러오는데 실패했습니다.')
    }
  }

  // 행 확장/축소 핸들러
  const handleRowExpand = async (id) => {
    const isExpanding = !expandedRowKeys.includes(id)

    if (isExpanding) {
      // 확장할 때 상세 정보가 없으면 API 호출
      const record = delegationData?.content?.find((item) => item.id === id)
      if (record && !detailData[record.id]) {
        await fetchDelegationDetail(record.id)
      }
    }

    const newExpandedKeys = expandedRowKeys.includes(id)
      ? expandedRowKeys.filter((k) => k !== id)
      : [...expandedRowKeys, id]
    setExpandedRowKeys(newExpandedKeys)
  }

  // 처방전 목록 페이지로 이동
  const handlePrescriptionList = (delegationId) => {
    navigate(`/delegated-claims/${delegationId}`)
  }

  // 확장된 행 렌더링
  const expandedRowRender = (record) => {
    const detail = detailData[record.id]
    const isLoading = loadingDetails[record.id]

    // 로딩 중일 때
    if (isLoading) {
      return (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#F6F7FA',
            borderRadius: '8px',
          }}
        >
          <Spin size="large" />
          <div style={{ color: '#6D747A', marginTop: '16px' }}>
            상세 정보를 불러오는 중...
          </div>
        </div>
      )
    }

    // 상세 정보가 없을 때
    if (!detail) {
      return (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#F6F7FA',
            borderRadius: '8px',
          }}
        >
          <div style={{ color: '#6D747A' }}>
            상세 정보를 불러올 수 없습니다.
          </div>
        </div>
      )
    }

    return (
      <div
        style={{
          padding: '20px',
          backgroundColor: '#F6F7FA',
          borderRadius: '8px',
        }}
      >
        {/* 다운로드 버튼들 - 2x2 그리드 */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#FFF',
                border: '1px solid #A4ADB4',
                borderRadius: '8px',
                minHeight: '70px',
              }}
            >
              <span
                style={{
                  color: '#6D747A',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                신분증
              </span>
              {detail.documents.identityCard ? (
                <Button
                  style={{
                    backgroundColor: '#FFF',
                    borderColor: '#A4ADB4',
                    color: '#6D747A',
                  }}
                  icon={<span>📄</span>}
                  onClick={() => {
                    if (detail.documents.identityCardUrl) {
                      window.open(detail.documents.identityCardUrl, '_blank')
                    }
                  }}
                >
                  다운로드
                </Button>
              ) : (
                <span
                  style={{
                    color: '#EB516F',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  미제출
                </span>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#FFF',
                border: '1px solid #A4ADB4',
                borderRadius: '8px',
                minHeight: '70px',
              }}
            >
              <span
                style={{
                  color: '#6D747A',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                위임증
              </span>
              {detail.document ? (
                <Button
                  style={{
                    backgroundColor: '#FFF',
                    borderColor: '#A4ADB4',
                    color: '#6D747A',
                  }}
                  icon={<span>📄</span>}
                  onClick={() => {
                    if (detail.document) {
                      window.open(detail.document, '_blank')
                    }
                  }}
                >
                  다운로드
                </Button>
              ) : (
                <span
                  style={{
                    color: '#EB516F',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  미제출
                </span>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#FFF',
                border: '1px solid #A4ADB4',
                borderRadius: '8px',
                minHeight: '70px',
              }}
            >
              <span
                style={{
                  color: '#6D747A',
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                차상위 증빙 서류
              </span>
              {detail.documents.lowIncomeProof ? (
                <Button
                  style={{
                    backgroundColor: '#FFF',
                    borderColor: '#A4ADB4',
                    color: '#6D747A',
                  }}
                  icon={<span>📄</span>}
                  onClick={() => {
                    if (detail.documents.lowIncomeUrl) {
                      window.open(detail.documents.lowIncomeUrl, '_blank')
                    }
                  }}
                >
                  다운로드
                </Button>
              ) : (
                <span
                  style={{
                    color: '#EB516F',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  미제출
                </span>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '16px',
                backgroundColor: '#117FFA',
                border: '2px solid #117FFA',
                borderRadius: '8px',
                cursor: 'pointer',
                minHeight: '70px',
              }}
              onClick={() => handlePrescriptionList(record.id)}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <span
                  style={{
                    color: '#FFF',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                >
                  처방전 목록 확인
                </span>
              </div>
              <Button
                type="primary"
                style={{
                  backgroundColor: '#FFF',
                  borderColor: '#FFF',
                  color: '#117FFA',
                  fontSize: '12px',
                }}
              >
                상세보기 →
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    )
  }

  return (
    <div style={{ padding: '20px' }}>
      <div>
        <h2
          style={{
            color: '#191a1c',
            marginBottom: '20px',
            fontSize: '24px',
            fontWeight: 'bold',
          }}
        >
          위임 신청 관리
        </h2>

        <Table
          columns={columns}
          dataSource={delegationData?.content || []}
          rowKey="id"
          loading={isLoading}
          expandable={{
            expandedRowRender,
            expandedRowKeys,
            onExpand: (expanded, record) => handleRowExpand(record.id),
            showExpandColumn: false,
          }}
          pagination={{
            pageSize: delegationData?.pageable?.pageSize || 10,
            current: (delegationData?.pageable?.pageNumber || 0) + 1,
            total: delegationData?.totalElements || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              total > 0
                ? `${range[0]}-${range[1]} / ${total}건`
                : '데이터가 없습니다',
            onChange: (page) => fetchDelegatedClaimsList(page - 1),
          }}
          locale={{
            emptyText: (
              <div
                style={{ padding: '40px', textAlign: 'center', color: '#999' }}
              >
                <div style={{ fontSize: '16px', marginBottom: '8px' }}>
                  데이터가 없습니다
                </div>
                <div style={{ fontSize: '14px' }}>
                  위임 신청 데이터를 찾을 수 없습니다.
                </div>
              </div>
            ),
          }}
          scroll={{ x: 1200 }}
          style={{
            backgroundColor: '#FFF',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      </div>

      {/* 반려 사유 입력 모달 */}
      <Modal
        title="위임 반려 사유 입력"
        open={reject.isRejectModalOpen}
        onCancel={handleRejectModalClose}
        footer={[
          <Button key="cancel" onClick={handleRejectModalClose}>
            취소하기
          </Button>,
          <Button
            key="reject"
            type="primary"
            danger
            onClick={handleRejectConfirm}
            loading={isLoading}
          >
            반려하기
          </Button>,
        ]}
        width={500}
      >
        <div style={{ margin: '16px 0' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              fontWeight: 'bold',
            }}
          >
            반려 사유를 입력해주세요. {'\n'}해당 내용은 알림톡에 그대로
            반영됩니다.
          </label>
          <Input.TextArea
            value={reject.rejectReason}
            onChange={(e) => {
              setReject({ ...reject, rejectReason: e.target.value })
            }}
            placeholder="반려 사유를 상세히 입력해주세요"
            rows={4}
            maxLength={500}
            showCount
          />
        </div>
      </Modal>
    </div>
  )
}

export default DelegatedClaimsPage
